import React, { useState, useRef, useEffect } from 'react';
import { useMessageQueue } from '../../Utils/messageQueue/useMessageQueue';
import { QueuedMessage } from '../../GlobalStore/Slices/DynamicStateObjSlice';
import { useAppStore } from '../../GlobalStore/GlobalStore';

interface MessageQueueManagerProps {
    isOpen: boolean;
    onClose: () => void;
}

const MessageQueueManager: React.FC<MessageQueueManagerProps> = ({ isOpen, onClose }) => {
    const { messageQueue, removeMessage, reorderMessage, clearQueue } = useMessageQueue();
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const { canSubmit } = dynamicStateObj;
    const modalRef = useRef<HTMLDivElement>(null);

    // Handle click outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.setData('text/plain', index.toString());
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        if (draggedIndex !== null && draggedIndex !== dropIndex) {
            reorderMessage(draggedIndex, dropIndex);
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleMoveUp = (index: number) => {
        if (index > 0) {
            reorderMessage(index, index - 1);
        }
    };

    const handleMoveDown = (index: number) => {
        if (index < messageQueue.length - 1) {
            reorderMessage(index, index + 1);
        }
    };

    const formatTimestamp = (timestamp: number) => {
        return new Date(timestamp).toLocaleTimeString();
    };

    const truncateText = (text: string, maxLength: number = 100) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div
                ref={modalRef}
                className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            >
                <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                        Message Queue{' '}
                        <span className="text-sm font-normal text-neutral-500 dark:text-neutral-400">
                            ({messageQueue.length} messages)
                        </span>
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                    >
                        ✕
                    </button>
                </div>

                <div className="p-4">
                    <div className="mb-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${canSubmit ? 'bg-green-500' : 'bg-red-500'}`}></div>
                            <span className="text-sm text-neutral-600 dark:text-neutral-400">
                                {canSubmit ? 'Ready to send' : 'Waiting to send'}
                            </span>
                        </div>

                        {messageQueue.length > 0 && (
                            <button
                                onClick={clearQueue}
                                className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            >
                                Clear all
                            </button>
                        )}
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                        {messageQueue.length === 0 ? (
                            <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                                No messages in queue
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {messageQueue.map((message: QueuedMessage, index: number) => (
                                    <div
                                        key={message.id}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragOver={(e) => handleDragOver(e, index)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={(e) => handleDrop(e, index)}
                                        className={`p-3 border rounded-lg cursor-move transition-colors ${
                                            draggedIndex === index 
                                                ? 'opacity-50 bg-blue-50 dark:bg-blue-900/20' 
                                                : dragOverIndex === index
                                                ? 'bg-blue-100 dark:bg-blue-800/30'
                                                : 'bg-neutral-50 dark:bg-neutral-700'
                                        } border-neutral-200 dark:border-neutral-600`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                                                        #{index + 1}
                                                    </span>
                                                    {message.isImportant && (
                                                        <span className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 px-2 py-0.5 rounded">
                                                            Important
                                                        </span>
                                                    )}
                                                    <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                                        {formatTimestamp(message.timestamp)}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-1">
                                                    {truncateText(message.text || 'No text')}
                                                </p>
                                                {Object.keys(message.files).length > 0 && (
                                                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                                                        {Object.keys(message.files).length} file(s) attached
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div className="flex items-center gap-1 ml-2">
                                                <button
                                                    onClick={() => handleMoveUp(index)}
                                                    disabled={index === 0}
                                                    className="p-1 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="Move up"
                                                >
                                                    ↑
                                                </button>
                                                <button
                                                    onClick={() => handleMoveDown(index)}
                                                    disabled={index === messageQueue.length - 1}
                                                    className="p-1 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="Move down"
                                                >
                                                    ↓
                                                </button>
                                                <button
                                                    onClick={() => removeMessage(message.id)}
                                                    className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                                    title="Remove"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MessageQueueManager;